"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Sparkles, Send, Loader as Loader2, User, Mail, Phone, Building2, Calendar, Clock, MessageSquare } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
const UNIVERSITY_TYPES = ["Public University", "Private University", "Community College", "Liberal Arts College", "Research University", "Online University"];
const TIME_SLOTS = ["Morning (9 am – 12 pm)", "Afternoon (12 pm – 4 pm)", "Evening (4 pm – 7 pm)"];
export default function AiStudyAssistantInput() {
  return <section data-section-id="118100" id="schedule-a-strategy-session-form" className="w-full min-h-[80vh] flex flex-col items-center justify-center py-16 px-4 md:px-8 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-4xl w-full space-y-12 relative z-10">
        <div className="text-center space-y-4 border-b border-border pb-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground font-serif">
            Schedule a Strategy Session
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-sans leading-relaxed">
            Let's discuss your objectives and design a roadmap for sustainable growth and operational excellence.
          </p>
        </div>

        <FormProvider formKey="schedule_strategy_session_form_scheduleyourstrategysession" formId="schedule_strategy_session_form_scheduleyourstrategysession" sectionName="schedule-a-strategy-session-form" defaultValues={{
        full_name: "",
        organization: "",
        email: "",
        phone: "",
        preferred_date: "",
        preferred_time: "",
        session_goals: ""
      }} validationRules={{
        full_name: {
          required: true
        },
        organization: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        preferred_date: {
          required: true
        },
        preferred_time: {
          required: true
        }
      }} submitText="Schedule Session" successMessage="Thank you for reaching out. We have received your request and will contact you shortly to confirm your strategy session.">
          <AssistantForm />
        </FormProvider>
      </div>
    </section>;
}
function AssistantForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-12 bg-white/70 backdrop-blur-[12px] border border-border rounded-none shadow-xl text-center space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="mx-auto w-20 h-20 bg-primary/10 rounded-none flex items-center justify-center mb-6">
          <Sparkles className="w-10 h-10 text-primary" />
        </div>
        <h3 className="text-3xl font-bold text-foreground font-serif">Session Requested</h3>
        <p className="text-lg text-muted-foreground font-sans max-w-md mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-8 rounded-none border-primary text-primary hover:bg-primary/5 px-8 h-12 uppercase tracking-widest font-bold">
          Submit Another Request
        </Button>
      </div>;
  }
  return <div className="animate-in fade-in slide-in-from-bottom-8 duration-1000">
      <Form onSubmit={handleSubmit} className="space-y-0">
        <div className="bg-white/70 backdrop-blur-[12px] border border-border rounded-none shadow-xl transition-all duration-500 focus-within:shadow-2xl focus-within:border-primary/20 overflow-hidden">
          <div className="p-6 md:p-10 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
              {}
              <FormField control={control} name="full_name" rules={{
              required: "Your full name is required."
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-xs font-bold uppercase tracking-widest text-primary flex items-center gap-2">
                      <User className="w-3.5 h-3.5" />
                      Your Full Name
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Jane Doe" className="h-12 rounded-none bg-background/50 border-border focus-visible:ring-primary/20 font-sans" {...field} />
                    </FormControl>
                    <FormDescription className="text-[11px] italic">
                      First and last name as you'd like us to address you.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>} />

              {}
              <FormField control={control} name="email" rules={{
              required: "Email address is required.",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email address."
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-xs font-bold uppercase tracking-widest text-primary flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5" />
                      Email Address
                    </FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="jane.doe@example.edu" className="h-12 rounded-none bg-background/50 border-border focus-visible:ring-primary/20 font-sans" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              {}
              <FormField control={control} name="phone" rules={{
              required: "Phone number is required."
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-xs font-bold uppercase tracking-widest text-primary flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5" />
                      Phone Number
                    </FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="+1 (555) 123-4567" className="h-12 rounded-none bg-background/50 border-border focus-visible:ring-primary/20 font-sans" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              {}
              <FormField control={control} name="organization" rules={{
              required: "Please select your institution type."
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-xs font-bold uppercase tracking-widest text-primary flex items-center gap-2">
                      <Building2 className="w-3.5 h-3.5" />
                      University Type
                    </FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-12 w-full rounded-none bg-background/50 border-border focus:ring-primary/20 font-sans">
                          <SelectValue placeholder="Select university type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {UNIVERSITY_TYPES.map((type, i) => <SelectItem key={type} value={type} data-index={i}>
                            {type}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormDescription className="text-[11px] italic">
                      Select the type of institution you represent.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>} />

              {}
              <FormField control={control} name="preferred_date" rules={{
              required: "Please select a preferred date."
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-xs font-bold uppercase tracking-widest text-primary flex items-center gap-2">
                      <SafeHydrate><Calendar className="w-3.5 h-3.5" /></SafeHydrate>
                      Preferred Date
                    </FormLabel>
                    <FormControl>
                      <Input type="date" className="h-12 rounded-none bg-background/50 border-border focus-visible:ring-primary/20 font-sans" {...field} />
                    </FormControl>
                    <FormDescription className="text-[11px] italic">
                      Select a date that works best for you.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>} />

              {}
              <FormField control={control} name="preferred_time" rules={{
              required: "Please select a preferred time slot."
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-xs font-bold uppercase tracking-widest text-primary flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5" />
                      Preferred Time of Day
                    </FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-12 w-full rounded-none bg-background/50 border-border focus:ring-primary/20 font-sans">
                          <SelectValue placeholder="Select a time slot" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TIME_SLOTS.map((time, i) => <SelectItem key={time} value={time} data-index={i}>
                            {time}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />
            </div>

            {}
            <FormField control={control} name="session_goals" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-xs font-bold uppercase tracking-widest text-primary flex items-center gap-2">
                    <MessageSquare className="w-3.5 h-3.5" />
                    What would you like to achieve in this session?
                  </FormLabel>
                  <FormControl>
                    <Textarea placeholder="Briefly describe your goals or challenges..." className="min-h-[120px] rounded-none bg-background/50 border-border focus-visible:ring-primary/20 font-sans p-4" {...field} />
                  </FormControl>
                  <FormDescription className="text-[11px] italic">
                    Helps us tailor the conversation to your needs.
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div className="bg-muted/30 p-4 md:p-6 border-t border-border">
            <Button type="submit" disabled={isSubmitting} className="w-full h-16 text-lg font-bold uppercase tracking-widest rounded-none bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg transition-all duration-300 group">
              {isSubmitting ? <>
                  <Loader2 className="animate-spin mr-3 w-6 h-6" />
                  Processing...
                </> : <>
                  Schedule Strategy Session
                  <Send className="ml-3 w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </>}
            </Button>
          </div>
        </div>

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none text-center mt-6">
            <p className="text-destructive text-sm font-bold uppercase tracking-wide">{submitError}</p>
          </div>}
      </Form>
    </div>;
}